import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import {
  FaChevronDown,
  FaFileAlt,
  FaWhatsapp,
  FaPhoneAlt,
  FaCheckCircle,
  FaArrowRight,
  FaBriefcase,
  FaPlaneDeparture,
  FaGraduationCap,
  FaBuilding,
  FaFileSignature,
  FaIdCard,
  FaPassport,
  FaPaperPlane,
} from "react-icons/fa";

/* ─────────────────────────────────────────────
   SERVICE DATA
───────────────────────────────────────────── */
const servicesData = {
  "work-visa": {
    Icon: FaBriefcase,
    color: "blue",
    title: "Work Visa Assistance",
    tagline: "Employment & Skilled Worker Visas",
    hero: "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2069&auto=format&fit=crop",
    description:
      "Our immigration consultants assist professionals in securing employment visas and work permits across multiple countries. We navigate complex immigration systems so you can focus on your career.",
    points: [
      { title: "Work Visa Eligibility Assessment", description: "Our experts evaluate your qualifications and determine the best work visa pathways available to you." },
      { title: "Employer Sponsorship Guidance", description: "We guide applicants and employers through sponsorship requirements, legal obligations and procedures." },
      { title: "Complete Documentation Preparation", description: "Our consultants prepare and review all documents required for a successful visa submission." },
      { title: "Visa Application Submission", description: "We manage the full visa application process and submit documents to the relevant authorities on your behalf." },
      { title: "Application Tracking & Updates", description: "We monitor your application progress and keep you fully informed at every stage." },
      { title: "Post-Approval Support", description: "Once approved, we provide guidance on arrival formalities, work permit registration and next steps." },
    ],
    documents: [
      "Valid Passport (min. 6 months validity)",
      "Educational Certificates",
      "Employment Experience Letters",
      "Updated Resume / CV",
      "Passport Size Photographs",
      "Bank Statements",
    ],
    faq: [
      { q: "How long does a work visa process take?", a: "Processing time varies by country and immigration program — typically between 3 and 12 weeks. We'll give you an accurate estimate during your consultation." },
      { q: "Do you assist with documentation?", a: "Yes, our team prepares, reviews and verifies every document before submission to ensure accuracy and completeness." },
      { q: "Can you help if my previous application was refused?", a: "Yes. We review refused applications, identify the issues and rebuild a stronger case for resubmission." },
      { q: "Do you track visa applications?", a: "Absolutely — we monitor your application status and provide regular updates throughout the entire process." },
    ],
    countries: ["Canada", "United Kingdom", "Australia", "Germany", "Singapore"],
    processingTime: "3–12 weeks",
    successRate: "98%",
  },

  "student-visa": {
    Icon: FaGraduationCap,
    color: "green",
    title: "Student Visa Services",
    tagline: "Study Abroad Visa Support",
    hero: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop",
    description:
      "We guide students from university selection all the way through to visa approval. Our consultants have helped hundreds of students realise their international education dreams.",
    points: [
      { title: "University Selection Guidance", description: "Our consultants help students identify universities and programs that align with their academic goals and career plans." },
      { title: "Student Visa Eligibility Assessment", description: "We evaluate your academic qualifications, financials and other criteria for the relevant student visa program." },
      { title: "Admission Documentation Support", description: "We prepare and review all documents required for university admission and visa submission." },
      { title: "Statement of Purpose Guidance", description: "Our team helps craft a compelling Statement of Purpose tailored to your chosen university and program." },
      { title: "Visa Interview Preparation", description: "We conduct mock interviews and provide targeted coaching for your visa or university interviews." },
      { title: "Pre-Departure Briefing", description: "We brief you on arrival procedures, accommodation, student rights and settling into your new country." },
    ],
    documents: [
      "Valid Passport",
      "Offer / Admission Letter from University",
      "Academic Certificates & Transcripts",
      "Proof of Funds / Bank Statements",
      "English Proficiency Test (IELTS/TOEFL)",
      "Passport Size Photographs",
    ],
    faq: [
      { q: "Do you help choose universities?", a: "Yes — we assess your profile, grades and interests and recommend suitable universities and programs." },
      { q: "Can you help with the Statement of Purpose?", a: "Yes, our consultants provide full guidance and review of your SOP to make it as strong as possible." },
      { q: "Do you assist with visa interviews?", a: "Absolutely — we run mock interviews and provide in-depth coaching to prepare you for any interview questions." },
      { q: "What if I don't meet the financial requirements?", a: "We explore alternative options such as scholarships, sponsorship letters and acceptable sponsor arrangements." },
    ],
    countries: ["Canada", "United Kingdom", "Australia", "Germany", "New Zealand"],
    processingTime: "4–10 weeks",
    successRate: "97%",
  },

  "travel-visa": {
    Icon: FaPlaneDeparture,
    color: "sky",
    title: "Tourist & Travel Visas",
    tagline: "Short-Stay & Visit Visas",
    hero: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?q=80&w=2070&auto=format&fit=crop",
    description:
      "Fast, reliable tourist and visit visa processing for individuals, couples and families. We handle all documentation so your trip planning stays completely stress-free.",
    points: [
      { title: "Tourist Visa Consultation", description: "We advise on visa requirements, eligibility conditions and documentation for your intended destination." },
      { title: "Document Preparation & Checklist", description: "Our experts prepare a precise document checklist and review everything before embassy submission." },
      { title: "Travel Itinerary Assistance", description: "We assist you in preparing a credible travel itinerary and hotel booking documents." },
      { title: "Visa Application Submission", description: "We file your tourist visa application with the embassy and handle all follow-up communications." },
      { title: "Urgent / Rush Processing", description: "For time-sensitive travel, we guide you through expedited visa procedures where available." },
    ],
    documents: [
      "Valid Passport",
      "Travel Itinerary",
      "Hotel / Accommodation Booking",
      "Proof of Funds / Bank Statements",
      "Return Ticket Confirmation",
      "Passport Size Photographs",
    ],
    faq: [
      { q: "How long does a tourist visa take?", a: "Processing time depends on the destination embassy — typically between 1 and 4 weeks. We'll advise you at consultation." },
      { q: "Can you assist with urgent travel visas?", a: "Yes — we guide clients through expedited processing options when urgent travel is required." },
      { q: "What if my tourist visa is refused?", a: "We review the refusal reason and help you build a stronger reapplication with the right documentation." },
    ],
    countries: ["USA", "UK", "Schengen Zone", "UAE", "Australia"],
    processingTime: "1–4 weeks",
    successRate: "96%",
  },

  "business-visa": {
    Icon: FaBuilding,
    color: "purple",
    title: "Business Visa Support",
    tagline: "Investor & Entrepreneur Visas",
    hero: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop",
    description:
      "We assist entrepreneurs, investors and corporate professionals with international business immigration. From investor visas to corporate relocation — we cover it all.",
    points: [
      { title: "Business Visa Consultation", description: "Our experts guide entrepreneurs and professionals through available business immigration programs globally." },
      { title: "Investor Visa Guidance", description: "We assist investors with visa pathways designed for global investment and business expansion." },
      { title: "Corporate Documentation Preparation", description: "Our consultants prepare financial statements, company documents and all supporting materials required." },
      { title: "Application Submission & Tracking", description: "We manage submission of business visa applications with immigration authorities and track progress." },
      { title: "Corporate Relocation Support", description: "We assist companies relocating employees internationally with the appropriate work and business visas." },
    ],
    documents: [
      "Valid Passport",
      "Business Registration Documents",
      "Financial Statements",
      "Investment Proof / Bank Statements",
      "Company Profile / Business Plan",
      "Passport Size Photographs",
    ],
    faq: [
      { q: "Can entrepreneurs apply for business visas?", a: "Yes — entrepreneurs and investors can apply through multiple dedicated immigration programs. We identify the best fit." },
      { q: "Do you assist with company documentation?", a: "Yes — we prepare and review all business and financial documents required for your visa application." },
      { q: "Can you assist with corporate employee relocation?", a: "Absolutely — we provide end-to-end immigration support for companies relocating staff internationally." },
    ],
    countries: ["UAE", "United Kingdom", "Singapore", "Canada", "Germany"],
    processingTime: "3–8 weeks",
    successRate: "98%",
  },

  attestation: {
    Icon: FaFileSignature,
    color: "orange",
    title: "Certificate Attestation",
    tagline: "Document Verification & Legalization",
    hero: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop",
    description:
      "Reliable attestation services for educational, personal and commercial documents. We ensure your documents are legally recognised and accepted in any country worldwide.",
    points: [
      { title: "Educational Certificate Attestation", description: "We verify and attest academic degrees and certificates for international employment or further study." },
      { title: "Marriage & Personal Document Attestation", description: "Our services include attestation of marriage certificates and other personal documents required for immigration." },
      { title: "Commercial Document Attestation", description: "We assist businesses with attestation of commercial and corporate documents for international use." },
      { title: "Embassy Legalization", description: "We coordinate document legalization with the relevant embassies and government authorities." },
      { title: "MOFA Attestation (UAE)", description: "We handle Ministry of Foreign Affairs attestation for documents to be used within the UAE." },
    ],
    documents: [
      "Original Certificates / Documents",
      "Clear Passport Copy",
      "Authorization Letter (if applicable)",
      "Previous Attestation Certificates",
      "Application Form",
    ],
    faq: [
      { q: "What is certificate attestation?", a: "It is the official process of verifying documents so they are legally accepted by authorities in another country." },
      { q: "How long does attestation take?", a: "Processing time varies by document type and issuing country — typically 1 to 3 weeks." },
      { q: "Can you attest documents from any country?", a: "Yes — we handle attestation for documents issued from most countries worldwide." },
    ],
    countries: ["UAE (MOFA)", "GCC Countries", "India", "Pakistan", "Worldwide"],
    processingTime: "1–3 weeks",
    successRate: "99%",
  },

  "trade-license": {
    Icon: FaIdCard,
    color: "red",
    title: "Trade License Services",
    tagline: "UAE Company Formation & Business Setup",
    hero: "https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=2070&auto=format&fit=crop",
    description:
      "Full-service company formation and trade license processing in the UAE. We handle all government approvals, registrations and documentation from start to finish.",
    points: [
      { title: "Company Formation Consultation", description: "Our consultants guide entrepreneurs through legal structures, jurisdiction selection and company formation options in the UAE." },
      { title: "Trade License Registration", description: "We manage the complete process of obtaining trade licenses from the relevant UAE government authorities." },
      { title: "Freezone & Mainland Setup", description: "We assist with both Dubai Mainland and Freezone company setups depending on your business activity." },
      { title: "Business Documentation Preparation", description: "Our team prepares all documentation required for company and license registration." },
      { title: "Government Approval Coordination", description: "We liaise with government departments to ensure approvals are completed efficiently and on time." },
      { title: "Visa & PRO Services", description: "We assist with investor and employee visa processing as part of your business setup package." },
    ],
    documents: [
      "Passport Copy (all shareholders)",
      "Passport Size Photographs",
      "Business Activity Description",
      "Shareholder / Partner Documents",
      "Office Address / Lease Agreement",
      "No Objection Certificate (if applicable)",
    ],
    faq: [
      { q: "How long does trade license registration take?", a: "UAE trade license registration typically takes between a few days and 2–4 weeks, depending on the business type and authority." },
      { q: "What's the difference between Mainland and Freezone?", a: "Mainland companies can trade anywhere in the UAE, while Freezone companies offer full foreign ownership but may have trade restrictions." },
      { q: "Do you assist with company formation?", a: "Yes — we provide full end-to-end business setup services including license, visa and office arrangement." },
    ],
    countries: ["Dubai", "Abu Dhabi", "Sharjah", "UAE Freezones"],
    processingTime: "1–4 weeks",
    successRate: "99%",
  },
};

const COLOR_MAP = {
  blue: { light: "bg-blue-50", icon: "text-blue-600", border: "border-blue-200", btn: "bg-blue-600 hover:bg-blue-700", badge: "bg-blue-100 text-blue-700", ring: "ring-blue-600" },
  sky: { light: "bg-sky-50", icon: "text-sky-600", border: "border-sky-200", btn: "bg-sky-600 hover:bg-sky-700", badge: "bg-sky-100 text-sky-700", ring: "ring-sky-600" },
  green: { light: "bg-green-50", icon: "text-green-600", border: "border-green-200", btn: "bg-green-600 hover:bg-green-700", badge: "bg-green-100 text-green-700", ring: "ring-green-600" },
  purple: { light: "bg-purple-50", icon: "text-purple-600", border: "border-purple-200", btn: "bg-purple-600 hover:bg-purple-700", badge: "bg-purple-100 text-purple-700", ring: "ring-purple-600" },
  orange: { light: "bg-orange-50", icon: "text-orange-500", border: "border-orange-200", btn: "bg-orange-500 hover:bg-orange-600", badge: "bg-orange-100 text-orange-600", ring: "ring-orange-500" },
  red: { light: "bg-red-50", icon: "text-red-500", border: "border-red-200", btn: "bg-red-600 hover:bg-red-700", badge: "bg-red-100 text-red-700", ring: "ring-red-600" },
};

const serviceList = Object.keys(servicesData);

const SERVICE_LABELS = {
  "work-visa": "Work Visa",
  "travel-visa": "Travel Visa",
  "student-visa": "Student Visa",
  "business-visa": "Business Visa",
  attestation: "Attestation",
  "trade-license": "Trade License",
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
function ServiceDetail() {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 py-20">
        <Helmet>
          <title>Service Not Found | Lumis Documents Clearing Dubai</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <FaPassport className="text-6xl text-gray-300 mb-6" />
        <h2 className="text-3xl font-bold text-gray-800 mb-3">Service Not Found</h2>
        <p className="text-gray-500 mb-8">The service you're looking for doesn't exist or may have been moved.</p>
        <Link to="/services" className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition">
          View All Services
        </Link>
      </div>
    );
  }

  const c = COLOR_MAP[service.color];
  const related = serviceList.filter((id) => id !== serviceId).slice(0, 4);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name && formData.email) setSubmitted(true);
  };

  return (
    <div>
      <Helmet>
        <title>{service.title} | Lumis Documents Clearing Dubai</title>
        <meta name="description" content={service.description} />
      </Helmet>
      {/* ── HERO ── */}
      <section
        className="h-[460px] flex items-end text-white bg-cover bg-center relative"
        style={{ backgroundImage: `url(${service.hero})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative w-full max-w-7xl mx-auto px-8 pb-14">
          {/* BREADCRUMB */}
          <div className="text-gray-300 text-sm mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition">Services</Link>
            <span>/</span>
            <span className="text-white font-medium">{service.title}</span>
          </div>

          {/* TAGLINE */}
          <p className={`text-sm font-semibold uppercase tracking-wider mb-3 ${c.icon}`}>
            {service.tagline}
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 max-w-2xl">
            {service.title}
          </h1>

          <p className="text-gray-200 max-w-xl leading-relaxed">
            {service.description}
          </p>

          {/* QUICK STATS PILLS */}
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-sm px-4 py-1.5 rounded-full">
              ⏱ Processing: {service.processingTime}
            </span>
            <span className="flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-sm px-4 py-1.5 rounded-full">
              ✅ Success Rate: {service.successRate}
            </span>
            <span className="flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-sm px-4 py-1.5 rounded-full">
              🌍 {service.countries.slice(0, 3).join(" · ")} {service.countries.length > 3 ? `+${service.countries.length - 3} more` : ""}
            </span>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + STICKY SIDEBAR ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-start">

            {/* LEFT — WHAT WE OFFER */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold mb-2">What We Offer</h2>
              <p className="text-gray-500 mb-10">
                A comprehensive service suite designed to make your immigration journey smooth and successful.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {service.points.map((point, i) => (
                  <div
                    key={i}
                    className={`group bg-white border ${c.border} rounded-xl p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition duration-300`}
                  >
                    <div className={`w-10 h-10 ${c.light} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition`}>
                      <FaCheckCircle className={`${c.icon} text-lg`} />
                    </div>
                    <h3 className={`font-bold text-base mb-2 group-hover:${c.icon} transition`}>
                      {point.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{point.description}</p>
                  </div>
                ))}
              </div>

              {/* COUNTRIES WE COVER */}
              <div className={`mt-10 ${c.light} rounded-2xl p-7 border ${c.border}`}>
                <h3 className="font-bold text-lg mb-4">Countries We Cover</h3>
                <div className="flex flex-wrap gap-3">
                  {service.countries.map((country) => (
                    <span
                      key={country}
                      className={`${c.badge} text-sm font-medium px-4 py-1.5 rounded-full`}
                    >
                      🌍 {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — STICKY CONSULTATION PANEL */}
            <div className="lg:sticky lg:top-24 h-fit flex flex-col gap-6">

              {/* CONSULTATION FORM */}
              <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
                <div className={`${c.light} border-b ${c.border} px-7 py-5`}>
                  <h3 className="text-xl font-bold text-gray-800">Free Consultation</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Speak with an expert about your {service.title.toLowerCase()}.
                  </p>
                </div>

                <div className="p-7">
                  {submitted ? (
                    <div className="text-center py-6">
                      <FaCheckCircle className="text-green-500 text-5xl mx-auto mb-4" />
                      <h4 className="font-bold text-gray-800 mb-2">Request Received!</h4>
                      <p className="text-gray-500 text-sm">We'll contact you within 24 hours.</p>
                      <button
                        onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", message: "" }); }}
                        className="mt-5 text-sm text-blue-600 hover:underline"
                      >
                        Send another request
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                      <input
                        type="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      />
                      <textarea
                        placeholder="Your Message (optional)"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={3}
                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none"
                      />
                      <button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 shadow"
                      >
                        <FaPaperPlane className="text-sm" />
                        Request Consultation
                      </button>
                    </form>
                  )}
                </div>

                <div className="px-7 pb-7">
                  <a
                    href="https://wa.me/971547662665"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 border-2 border-green-500 text-green-600 py-3 rounded-xl font-semibold hover:bg-green-500 hover:text-white transition"
                  >
                    <FaWhatsapp size={18} />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>

              {/* QUICK CONTACT CARD */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h4 className="font-bold text-gray-800 mb-4">Quick Contact</h4>
                <div className="flex flex-col gap-3">
                  <a
                    href="tel:+971547662665"
                    className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition"
                  >
                    <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center">
                      <FaPhoneAlt className="text-blue-600 text-sm" />
                    </div>
                    +971 54 766 2665
                  </a>
                  <a
                    href="mailto:info@lumisdocumentsclearing.com"
                    className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition"
                  >
                    <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center">
                      <FaFileAlt className="text-blue-600 text-sm" />
                    </div>
                    info@lumisdocumentsclearing.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DOCUMENTS REQUIRED ── */}
      <section className="bg-gray-100 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">Documents Required</h2>
          <p className="text-center text-gray-500 mb-14">
            We'll guide you through collecting every document on this checklist
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {service.documents.map((doc, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition flex items-center gap-4 border ${c.border}`}
              >
                <div className={`w-10 h-10 ${c.light} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <FaFileAlt className={`${c.icon}`} />
                </div>
                <p className="text-sm font-medium text-gray-700">{doc}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-sm mt-8">
            * Additional documents may be required depending on your specific situation. Our consultants will provide a full personalised checklist during your consultation.
          </p>
        </div>
      </section>

      {/* ── PROCESS TIMELINE ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">Our Process</h2>
          <p className="text-center text-gray-500 mb-14">
            A clear, transparent journey from inquiry to approval
          </p>

          <div className="grid md:grid-cols-4 gap-8 text-center relative">
            {/* CONNECTING LINE */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-100 z-0" />

            {[
              { step: "01", title: "Free Consultation", desc: "We assess your profile, eligibility and the best visa pathway for you." },
              { step: "02", title: "Document Collection", desc: "We provide a checklist and verify every document you provide before submission." },
              { step: "03", title: "Application Filing", desc: "We submit your complete application and manage all follow-ups with authorities." },
              { step: "04", title: "Visa Approved!", desc: "Receive your approval and prepare for the next chapter of your journey." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="relative z-10">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5 shadow-lg">
                  {step}
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Common questions about our {service.title.toLowerCase()}
          </p>

          <div className="flex flex-col gap-4">
            {service.faq.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex justify-between items-center px-7 py-5 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-gray-800 pr-4">{item.q}</span>
                  <FaChevronDown
                    className={`flex-shrink-0 text-blue-600 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""
                      }`}
                  />
                </button>

                <div
                  className={`transition-all duration-300 overflow-hidden ${openFaq === i ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                  <p className="text-gray-600 text-sm px-7 pb-6 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RELATED SERVICES ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-3">Related Services</h2>
          <p className="text-center text-gray-500 mb-14">
            Explore other immigration services we offer
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {related.map((id) => {
              const s = servicesData[id];
              const sc = COLOR_MAP[s.color];
              return (
                <Link
                  key={id}
                  to={`/services/${id}`}
                  className="group bg-white border border-gray-100 rounded-xl p-6 shadow hover:shadow-xl hover:-translate-y-2 transition duration-300 text-center"
                >
                  <div className={`w-12 h-12 ${sc.light} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition`}>
                    <s.Icon className={`${sc.icon} text-xl`} />
                  </div>
                  <h3 className="font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition text-sm">
                    {SERVICE_LABELS[id]}
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">{s.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-blue-600 text-xs font-semibold group-hover:gap-2 transition">
                    Learn More <FaArrowRight className="text-xs" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="bg-blue-600 text-white py-20 text-center">
        <h2 className="text-3xl font-bold mb-4">
          Ready to Start Your {service.title}?
        </h2>
        <p className="text-blue-100 mb-8 max-w-xl mx-auto">
          Contact our expert consultants today. We offer a free initial consultation and guide you through every step of the process.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/contact"
            className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-md flex items-center gap-2"
          >
            Get Free Consultation <FaArrowRight />
          </Link>
          <a
            href="https://wa.me/971547662665"
            target="_blank"
            rel="noreferrer"
            className="border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition flex items-center gap-2"
          >
            <FaWhatsapp size={18} /> Chat on WhatsApp
          </a>
        </div>
      </section>

      {/* ── FLOATING WIDGET ── */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">Chat with us on WhatsApp</p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>
    </div>
  );
}

export default ServiceDetail;
